/**
 * Kaching Bundles Custom Color Swatch Handler
 * =========================================
 * 
 * This script enhances the Kaching Bundles interface by managing color swatches
 * and their display in swatch buttons. It provides real-time color updates
 * and handles dynamic content loading.
 * 
 * Functionality:
 * - Monitors and applies color swatch changes to swatch buttons
 * - Handles dynamically loaded content through mutation observers
 * - Ensures color swatches are properly displayed even with async loading
 * 
 * Implementation Details:
 * - Uses MutationObserver to watch for both initial load and dynamic updates
 * - Applies colors from CSS custom properties (--kaching-swatch-color)
 * - Includes fallback timeout for edge cases in loading scenarios
 * 
 * Target Elements:
 * - .kaching-bundles__swatch-buttons: Main swatch button containers
 * - .kaching-bundles__swatch: Individual color swatch elements
 */

// Gallery filter for Kaching Bundle swatches
const updateGallery = (clickedColor, clickedSwatch) => {
  if (!clickedColor || !clickedSwatch) return;
  
  // Get all selected colors from both variant sets
  const selectedColors = new Set();
  
  // Find which bundle bar we're in
  const bundleBar = clickedSwatch.closest('.kaching-bundles__bar');
  const allBars = Array.from(document.querySelectorAll('.kaching-bundles__bar'));
  const isSecondBundle = allBars.indexOf(bundleBar) === 1;
  
  if (isSecondBundle) {
    // We're in the Buy 2 bundle - get colors from both selected swatches
    const swatchGroups = bundleBar.querySelectorAll('.kaching-bundles__swatch-buttons');
    swatchGroups.forEach(group => {
      const selectedSwatch = group.querySelector('.kaching-bundles__swatch-buttons__button--selected');
      if (selectedSwatch) {
        const color = selectedSwatch.getAttribute('title');
        if (color) selectedColors.add(color.toLowerCase());
      }
    });
  } else {
    // Single dino - only use clicked color
    selectedColors.add(clickedColor.toLowerCase());
  }
  
  // Get all media items and thumbnails
  const mediaItems = document.querySelectorAll('.product__media-item');
  const thumbnails = document.querySelectorAll('.thumbnail-list__item');
  let firstVisible = null;
  
  // Function to check if an image matches any of the selected colors
  const matchesSelectedColors = (img) => {
    const altText = img.alt || '';
    const words = altText.toLowerCase().split(/\s+/);
    return [...selectedColors].some(color => words.includes(color));
  };
  
  // Filter main gallery items
  mediaItems.forEach(item => {
    const img = item.querySelector('img');
    if (!img) return;
    
    if (matchesSelectedColors(img)) {
      item.style.display = '';
      item.classList.remove('hidden');
      if (!firstVisible) {
        firstVisible = item;
        item.classList.add('is-active');
      } else {
        item.classList.remove('is-active');
      }
    } else {
      item.style.display = 'none';
      item.classList.add('hidden');
      item.classList.remove('is-active');
    }
  });

  // Filter thumbnails  
  thumbnails.forEach(thumbnail => {
    const img = thumbnail.querySelector('img');
    if (!img) return;
    
    if (matchesSelectedColors(img)) {
      thumbnail.style.display = '';
      thumbnail.classList.remove('hidden');
    } else {
      thumbnail.style.display = 'none';
      thumbnail.classList.add('hidden');
    }
  });
};

// Function to find the active bundle bar and its selected swatch
const getActiveBundleAndSwatch = () => {
  const checkedInput = document.querySelector('input[name*="kaching-bundles-deal"]:checked');
  if (!checkedInput) return null;
  
  const bundleBar = document.querySelector(`[data-deal-bar-id="${checkedInput.value}"]`);
  if (!bundleBar) return null;
  
  const selectedSwatch = bundleBar.querySelector('.kaching-bundles__swatch-buttons__button--selected');
  return selectedSwatch;
};

// Animation function for selected swatches
const animateSelectedSwatch = (swatch) => {
  if (!swatch) return;
  
  swatch.style.transform = 'scale(1.1)';
  swatch.style.transition = 'transform 0.2s ease';
  
  setTimeout(() => {
    swatch.style.transform = 'scale(1)';
  }, 200);
};

// Enhanced swatch click handler
document.addEventListener('click', (e) => {
  const swatch = e.target.closest('.kaching-bundles__swatch-buttons__button');
  if (swatch) {
    const color = swatch.getAttribute('title');
    
    if (color) {
      updateGallery(color, swatch);
      animateSelectedSwatch(swatch);
    }
  }
});

// Enhanced bundle selection change handler
document.addEventListener('change', (e) => {
  if (e.target.matches('input[name*="kaching-bundles-deal"]')) {
    // Small delay to let the bundle UI update
    setTimeout(() => {
      const selectedSwatch = getActiveBundleAndSwatch();
      if (selectedSwatch) {
        const color = selectedSwatch.getAttribute('title');
        if (color) {
          updateGallery(color, selectedSwatch);
        }
      }
    }, 100);
  }
});

// Listen for add to cart attempts
document.addEventListener('click', (e) => {
  if (e.target.matches('[type="submit"][name="add"], .btn[data-action*="add"]')) {
    console.log('🛒 ADD TO CART CLICKED');
    
    // Get the form being submitted
    const form = e.target.closest('form') || document.querySelector('product-form form, form[action*="cart"]');
    if (form) {
      const formData = new FormData(form);
      const products = [];
      
      for (let [key, value] of formData.entries()) {
        if (key === 'id') {
          products.push({ variantId: value, key: 'main-product' });
        } else if (key.includes('bundles') || key.includes('variant')) {
          products.push({ variantId: value, key: key });
        }
      }
      
      console.log('📦 Products being added:', products);
      console.log('📊 Total products:', products.length);
      
      // Check bundle selection
      const selectedBundle = document.querySelector('input[name*="kaching-bundles-deal"]:checked');
      if (selectedBundle) {
        const bundleName = document.querySelector(`[data-deal-bar-id="${selectedBundle.value}"] .kaching-bundles__bar-title`)?.textContent?.trim();
        console.log('🎯 Selected bundle:', bundleName || selectedBundle.value);
        
        if (bundleName && bundleName.includes('Buy 2') && products.length < 2) {
          console.log('⚠️ WARNING: Buy 2 bundle selected but only', products.length, 'product(s) found!');
        }
      }
    }
  }
});

// Listen for successful cart additions
document.addEventListener('submit', (e) => {
  if (e.target.matches('product-form form, [action*="cart"]')) {
    console.log('📝 CART FORM SUBMITTED');
    
    const formData = new FormData(e.target);
    const submittedData = {};
    
    for (let [key, value] of formData.entries()) {
      submittedData[key] = value;
    }
    
    console.log('📋 Form data:', submittedData);
  }
});

// Initial state logic - filter thumbnails on page load based on default variant
document.addEventListener('DOMContentLoaded', () => {
  // Wait a bit for all elements to be fully loaded
  setTimeout(() => {
    initializeDefaultFiltering();
  }, 500);
});

// Function to initialize filtering with default variant
function initializeDefaultFiltering() {
  // Try multiple approaches to find the default/selected color
  let defaultColor = null;
  let defaultSwatch = null;
  
  // Enhanced color detection - includes more color variations
  const getColorWords = () => [
    'blue', 'red', 'green', 'yellow', 'purple', 'pink', 'orange', 'black', 'white', 'gray', 'grey',
    'cyan', 'magenta', 'brown', 'navy', 'teal', 'lime', 'olive', 'maroon', 'silver', 'gold',
    'beige', 'tan', 'coral', 'salmon', 'turquoise', 'violet', 'indigo', 'crimson', 'azure', 'lavender'
  ];

  // Method 1: Look for pre-selected swatch in bundle interface
  const selectedSwatch = document.querySelector('.kaching-bundles__swatch-buttons__button--selected');
  if (selectedSwatch) {
    defaultColor = selectedSwatch.getAttribute('title');
    defaultSwatch = selectedSwatch;
  }

  // Method 2: If no pre-selected swatch, look for the first available swatch
  if (!defaultColor) {
    const firstSwatch = document.querySelector('.kaching-bundles__swatch-buttons__button');
    if (firstSwatch) {
      defaultColor = firstSwatch.getAttribute('title');
      defaultSwatch = firstSwatch;
      // Mark it as selected if it wasn't already
      firstSwatch.classList.add('kaching-bundles__swatch-buttons__button--selected');
    }
  }

  // Method 3: Look for selected variant in Shopify's variant selector
  if (!defaultColor) {
    const variantSelect = document.querySelector('variant-selects [data-selected-variant]');
    if (variantSelect) {
      try {
        const selectedVariant = JSON.parse(variantSelect.innerHTML);
        // Try to extract color from variant title or options
        if (selectedVariant && selectedVariant.title) {
          const variantTitle = selectedVariant.title.toLowerCase();
          const colorWords = getColorWords();
          for (const color of colorWords) {
            if (variantTitle.includes(color)) {
              defaultColor = color;
              break;
            }
          }
        }
      } catch (e) {
        // Silently continue if can't parse variant data
      }
    }
  }

  // Method 4: Look for selected Shopify variant selector (radio buttons or dropdowns)
  if (!defaultColor) {
    // Check for selected radio button in variant picker
    const selectedRadio = document.querySelector('variant-selects input[type="radio"]:checked');
    if (selectedRadio) {
      const colorValue = selectedRadio.value.toLowerCase();
      const colorWords = getColorWords();
      for (const color of colorWords) {
        if (colorValue.includes(color)) {
          defaultColor = color;
          break;
        }
      }
    }
    
    // Check for selected option in dropdown
    if (!defaultColor) {
      const selectedOption = document.querySelector('variant-selects select option:checked, variant-selects select option[selected]');
      if (selectedOption) {
        const optionText = selectedOption.textContent.toLowerCase();
        const colorWords = getColorWords();
        for (const color of colorWords) {
          if (optionText.includes(color)) {
            defaultColor = color;
            break;
          }
        }
      }
    }
  }

  // Method 5: Extract color from the first visible thumbnail's alt text
  if (!defaultColor) {
    const firstThumbnail = document.querySelector('.thumbnail-list__item img');
    if (firstThumbnail && firstThumbnail.alt) {
      const altText = firstThumbnail.alt.toLowerCase();
      const words = altText.split(/\s+/);
      const colorWords = getColorWords();
      for (const color of colorWords) {
        if (words.includes(color)) {
          defaultColor = color;
          break;
        }
      }
    }
  }

  // Apply the default filtering
  if (defaultColor && defaultSwatch) {
    updateGallery(defaultColor, defaultSwatch);
  } else if (defaultColor) {
    // Create a mock swatch element if we have color but no swatch
    const mockSwatch = document.createElement('div');
    mockSwatch.setAttribute('title', defaultColor);
    updateGallery(defaultColor, mockSwatch);
  } else {
    // Try one more time with a very broad search
    const allImages = document.querySelectorAll('img[alt*=" "]');
    for (const img of allImages) {
      if (img.alt) {
        const altText = img.alt.toLowerCase();
        const colorWords = getColorWords();
        for (const color of colorWords) {
          if (altText.includes(color)) {
            const mockSwatch = document.createElement('div');
            mockSwatch.setAttribute('title', color);
            updateGallery(color, mockSwatch);
            return; // Exit if we found something
          }
        }
      }
    }
  }
}

// Initialize animation for any pre-selected swatches
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.kaching-bundles__swatch-buttons__button--selected').forEach(swatch => {
    animateSelectedSwatch(swatch);
  });
});



